<template>
  <base-modal
  :title="title"
  style="max-width: 1200px; width: 100%; max-height: 90vh;"
  icon="mdi-table-large"
  :header-color="headerColor"
  >

   <!-- El margen va en el contenedor (mismo que .custom-data-table): Vuetify le pisa el del input. -->
   <div v-if="compactSearch" class="table-modal__buscar">
     <v-text-field
        v-model="search"
        label="BUSCAR"
        autofocus
        clearable
        outlined
        dense
        hide-details
        color="primary"
        append-icon="mdi-magnify"
      />
   </div>
   <form-text-field
      v-else
      v-model="search"
      label="BUSCAR"
      autofocus
      append-icon="mdi-magnify"
      style="margin: 0 1rem -10px ;"
    >
   </form-text-field>


    <div class="custom-data-table">
      <base-matrix-table
        :columnas-izquierda="columnasTabla"
        :columnas-derecha="columnasAccion"
        :filas="itemsPagina"
        clave-fila="n"
        :compacta="dense"
        @click:fila="selectRow"
      >
        <!-- Además de poder clickear la fila entera, este botón deja seleccionar el
             elemento explícitamente. `@click.stop` evita que el click dispare TAMBIÉN
             el `@click:fila` de la tabla (que ya llama a `selectRow` con la misma fila). -->
        <template #[`celda.seikoAccionSeleccionar`]="{ fila }">
          <v-btn
            icon
            small
            color="secondary"
            title="SELECCIONAR"
            @click.stop="selectRow(fila)"
          >
            <v-icon small>mdi-check-circle-outline</v-icon>
          </v-btn>
        </template>
      </base-matrix-table>

      <div class="text-center py-6 grey--text" v-if="itemsFiltrados.length === 0">
        SIN RESULTADOS
      </div>

      <div class="d-flex justify-end align-center mt-3" v-if="totalPaginas > 1">
        <span class="mr-3 grey--text text--darken-1">PÁGINA {{ paginaActual }} DE {{ totalPaginas }}</span>
        <v-btn icon :disabled="paginaActual === 1" @click="paginaActual--">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon :disabled="paginaActual === totalPaginas" @click="paginaActual++">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="footerProps.showFooter" class="table-modal__footer">
      <slot name="footer" :items="items" :headers="headers">
        <!-- Con `confirmar`, la fila elegida se muestra a la izquierda y el
             botón de confirmar queda a la derecha. -->
        <span v-if="footerProps.confirmar" class="table-modal__seleccion" aria-live="polite">
          {{ textoSeleccion }}
        </span>
        <v-btn
          :title="footerProps.title"
          :disabled="footerProps.confirmar && !seleccionada"
          :color="footerProps.confirmar ? '#EE680B' : undefined"
          :dark="footerProps.confirmar && !!seleccionada"
          type="button"
          @click="footerAction"
        >
          <v-icon :color="footerProps.confirmar ? undefined : '#aaa'">{{ footerProps.icon || 'mdi-plus' }}</v-icon>
        </v-btn>
      </slot>
    </div>

  </base-modal>
</template>

<script>
import BaseModal from '../BaseModal.vue';
import BaseMatrixTable from '../BaseMatrixTable.vue';
import FormTextField from '../FormInputs/FormTextField.vue';

export default {
  name: 'TableModal',
  components: { BaseModal, BaseMatrixTable, FormTextField },

  props: {
    headers:  { type: Array,  default: () => [] },
    items:    { type: Array,  default: () => [] },
    title:    { type: String, default: 'Tabla' },
    footerProps: {
      type: Object,
      default: () => ({ showFooter: false }),
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    headerColor: {
      type: String,
      default: '#002245',
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
    hideSelectAction: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    /** Buscador de menor alto (36px, no los 56px del campo de formulario), alineado al ancho de la tabla. */
    compactSearch: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['select', 'footer-action'],

  data() {
    return {
      search: '',
      paginaActual: 1,
      seleccionada: null,
    };
  },

  computed: {
    columnasTabla() {
      return this.headers
        .filter((h) => h.value !== 'seikoAccionSeleccionar')
        .map((h) => ({
          valor: h.value,
          texto: h.text,
          // Opcionales: solo las pantallas que los declaran cambian de aspecto.
          ...(h.alineacion ? { alineacion: h.alineacion } : {}),
          ...(h.alineacionEncabezado ? { alineacionEncabezado: h.alineacionEncabezado } : {}),
          ...(h.ajustarTexto ? { ajustarTexto: true } : {}),
          ...(h.anchoMinimo ? { anchoMinimo: h.anchoMinimo } : {}),
        }));
    },
    /**
     * Columna de acción (botón "seleccionar"): se agrega sola, una vez, para
     * no tener que repetirla en cada `headers` que arma cada pantalla que usa
     * `FormTableSelectField` (materias, gestiones, ítems de seguimiento, etc.).
     */
    columnasAccion() {
      return this.hideSelectAction ? [] : [{ valor: 'seikoAccionSeleccionar', texto: '' }];
    },
    textoSeleccion() {
      if (!this.seleccionada) return 'ELIJA UNA FILA';
      const campo = this.footerProps.campoSeleccion;
      return `SELECCIONADA: ${campo ? this.seleccionada[campo] : ''}`;
    },
    itemsFiltrados() {
      return this.filtrarPorTexto(this.items, this.search);
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.itemsFiltrados.length / this.itemsPerPage));
    },
    /** Se le inyecta `n` (numeración visible) a cada fila; también sirve de `clave-fila` única. */
    itemsPagina() {
      const inicio = (this.paginaActual - 1) * this.itemsPerPage;
      return this.itemsFiltrados
        .slice(inicio, inicio + this.itemsPerPage)
        .map((item, i) => ({ ...item, n: inicio + i + 1 }));
    },
  },

  watch: {
    itemsFiltrados() {
      this.paginaActual = 1;
    },
  },

  methods: {
    filtrarPorTexto(lista, texto) {
      const conDatos = lista.filter((fila) =>
        Object.values(fila).some(
          (valor) => valor != null && valor.toString().trim() !== '',
        ),
      );
      if (!texto) return conDatos;
      const termino = texto.toString().toUpperCase();
      return conDatos.filter((fila) =>
        Object.values(fila).some(
          (valor) => valor != null && valor.toString().toUpperCase().includes(termino),
        ),
      );
    },
    selectRow(item) {
      this.seleccionada = item;
      this.$emit('select', item);
    },
    footerAction() {
      this.$emit('footer-action');
    },
  },
};
</script>

<style scoped>
.seiko-search-wrapper {
  margin: 0 1rem;
  margin-bottom: 8px;

  --search-border:        #CCD3D9;          
  --search-border-focus:  #002245;          /* primary      */
  --search-bg:            #FFFFFF;
  --search-bg-focus:      #FFFFFF;
  --search-shadow-focus:  rgba(0, 34, 69, 0.12);
  --search-icon-color:    #99A6B3;          /* primary-300  */
  --search-icon-focus:    #002245;          /* primary      */
  --search-text:          #002245;          /* primary      */
  --search-placeholder:   #99A6B3;          /* primary-300  */
  --search-badge-bg:      #EE680B;          /* secondary    */
  --search-badge-text:    #FFFFFF;
  --search-clear-color:   #99A6B3;          /* primary-300  */
  --search-clear-hover:   #EE680B;          /* secondary    */
}

/* ── Campo de búsqueda ──────────────────────────────────────────────── */
.seiko-search-field {
  display: flex;
  align-items: center;
  gap: 8px;

  background-color: var(--search-bg);
  border: 1.5px solid var(--search-border);
  border-radius: 8px;
  padding: 0 12px;
  height: 42px;

  transition:
    border-color  0.2s ease,
    box-shadow    0.2s ease,
    background-color 0.2s ease;
}

.seiko-search-field--focused {
  border-color: var(--search-border-focus);
  box-shadow: 0 0 0 3px var(--search-shadow-focus);
  background-color: var(--search-bg-focus);
}

.seiko-search-icon-left {
  color: var(--search-icon-color) !important;
  flex-shrink: 0;
  transition: color 0.2s ease;
}
.seiko-search-field--focused .seiko-search-icon-left {
  color: var(--search-icon-focus) !important;
}

.seiko-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--search-text);
  min-width: 0; 
}
.seiko-search-input::placeholder {
  color: var(--search-placeholder);
  font-style: italic;
}

.seiko-search-clear {
  flex-shrink: 0;
  color: var(--search-clear-color) !important;
  transition: color 0.2s ease !important;
}
.seiko-search-clear:hover {
  color: var(--search-clear-hover) !important;
}

.seiko-search-badge {
  flex-shrink: 0;
  background-color: var(--search-badge-bg);
  color: var(--search-badge-text);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 7px;
  border-radius: 10px;
  line-height: 1.6;
  min-width: 22px;
  text-align: center;
}

.seiko-fade-enter-active,
.seiko-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.seiko-fade-enter,
.seiko-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.custom-data-table {
  margin: 0 1rem;
}

.table-modal__buscar {
  margin: 0 1rem 14px;
}

/* Más bajo que el dense de Vuetify (40px): 36px, con ícono y rótulo recentrados. */
.table-modal__buscar ::v-deep .v-input--dense > .v-input__control > .v-input__slot {
  min-height: 36px;
}
.table-modal__buscar ::v-deep .v-text-field--outlined.v-input--dense input {
  padding: 4px 0;
}
.table-modal__buscar ::v-deep .v-text-field--outlined.v-input--dense .v-label {
  top: 8px;
}
.table-modal__buscar ::v-deep .v-input__append-inner {
  margin-top: 6px;
}

.table-modal__footer {
  margin: 12px 1rem 0;
  padding-top: 8px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
}

.table-modal__seleccion {
  margin-right: auto;
  color: #002245;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>